// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

// Custom packages
import moment from 'moment';
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";

/**
 * Script start
 */
@Component({
  selector: "app-print-qr-dialog",
  templateUrl: "./print-qr-dialog.component.html",
  styleUrls: ["./print-qr-dialog.component.scss"]
})
export class PrintQrDialogComponent
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Edit item form

  qrCodeData = [];
  modulesData = [];
  year: string;
  docType = "";
  companyName = "";

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PrintQrDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    // private helperService: HelperService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    if (this.data && this.data.companyName) {
      this.companyName = this.data.companyName;
    }
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    // Get current year
    this.year = moment().year().toString();

    // Init form
    this.initForm();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    if (this.data) {
      if (this.data.qrCodes && this.data.qrCodes.length) {
        if (this.data.target && this.data.target === "qr") {
          this.data.qrCodes.forEach((qr: string) => {
            this.qrCodeData.push(qr);
          });
          if (this.data.docs && Array.isArray(this.data.docs) && this.data.docs.length > 0) {
            this.docType = this.data.docs[0].title;
          }
        } else if (this.data.target && this.data.target === "module") {
          // Prepare data before injection
          const topics = [];
          this.data.docs.forEach(doc => {
            if (doc.subAreaTitle) {
              if (topics.length && topics.includes(doc.subAreaTitle)) {
                return;
              }
              topics.push(doc.subAreaTitle);
            }
          });

          const uploadDate = new Date(this.data.uploadDate.toString()).toISOString();
          // Eliminate duplicates
          const docs = this.data.docs
            .filter((doc, index, self) => self.findIndex(t => t.key === doc.key) === index);

          // Create data object
          this.data.qrCodes.forEach((qr: string) => {
            const newElement = {
              qrCode: qr,
              docs,
              topics, // (eg. gestAmmTivoDipendenti, ...)
              structures: this.data.structures, // (eg. Carlo, ...)
              uploadDate,
            };
            this.modulesData.push(newElement);
          });
        } else {
          this.toastrService.error("No element injected")
        }
      }
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Prints the whole HTML contained in "dataToPrint" element
   *
   * @since 1.0.0
   */
  onPrint(): void {
    const printData = document.getElementById('dataToPrint').innerHTML;
    const openWindow = window.open("", "title", "attributes");
    openWindow.document.write(printData);
    openWindow.document.close();
    openWindow.focus();
    openWindow.print();
    openWindow.close();
  }
}
